import { inject, injectable } from 'tsyringe';
import { type IAgentUserRepository } from 'application/repositorySchemas/IAgentUserRepository';
import { type IAgentUserTokenRepository } from 'application/repositorySchemas/IAgentUserTokenRepository';
import { type IAgentLoginByEmailClient } from 'interfaceAdapter/gatewaySchemas/IAgentLoginByEmailClient';
import { type IAgentUserLogoutClient } from 'interfaceAdapter/gatewaySchemas/IAgentUserLogoutClient';
import {
    type IAgentUserProfileRegistrationClient,
    IPostInput as IProfileRegistrationInput,
} from 'interfaceAdapter/gatewaySchemas/IAgentUserProfileRegistrationClient';
import {
    type IEditAgentUserProfileClient,
    IPostInput as IEditProfileInput,
} from 'interfaceAdapter/gatewaySchemas/IEditAgentUserProfileClient';
import { type IAgentSendPasswordResetEmailClient } from 'interfaceAdapter/gatewaySchemas/IAgentSendPasswordResetEmailClient';
import { type IAgentUserPasswordClient } from 'interfaceAdapter/gatewaySchemas/IAgentUserPasswordClient';

@injectable()
export class AgentUserRepository implements IAgentUserRepository {
    public readonly token: string;

    constructor(
        @inject('IAgentUserTokenRepository')
        public readonly agentUserToken: IAgentUserTokenRepository,
        @inject('IAgentLoginByEmailClient')
        public readonly agentLoginByEmail: IAgentLoginByEmailClient,
        @inject('IAgentUserLogoutClient')
        public readonly agentUserLogoutClient: IAgentUserLogoutClient,
        @inject('IAgentUserProfileRegistrationClient')
        public readonly agentUserProfileRegistration: IAgentUserProfileRegistrationClient,
        @inject('IEditAgentUserProfileClient')
        public readonly editAgentUserProfile: IEditAgentUserProfileClient,
        @inject('IAgentSendPasswordResetEmailClient')
        public readonly agentSendPasswordResetEmailClient: IAgentSendPasswordResetEmailClient,
        @inject('IAgentUserPasswordResetClient')
        public readonly agentUserPasswordResetClient: IAgentUserPasswordClient,
    ) {
        this.token = this.agentUserToken.get() ?? '';
    }

    public loginByEmail = async (email: string, password: string) => {
        const { data, message } = await this.agentLoginByEmail.post({ email, password });
        if (message) {
            throw new Error(message);
        }
        this.agentUserToken.remove();
        this.agentUserToken.set(data.api_token);
        return data;
    };

    public logout = async () => {
        await this.agentUserLogoutClient.post({ token: this.token });
    };

    public registerProfile = async (agent_user: IProfileRegistrationInput['agent_user']) => {
        const { data, message } = await this.agentUserProfileRegistration.post({
            token: this.agentUserToken.get() || '',
            agent_user,
        });

        if (message) {
            throw new Error(message);
        }

        return data;
    };

    public editProfile = async (agent_user: IEditProfileInput['agent_user']) => {
        const { data, message } = await this.editAgentUserProfile.post({
            token: this.agentUserToken.get() || '',
            agent_user,
        });

        if (message) {
            throw new Error(message);
        }

        return data;
    };

    public sendResetPasswordEmail = async (email: string) => {
        const { message } = await this.agentSendPasswordResetEmailClient.post({ email });
        if (message) {
            throw new Error(message);
        }
    };

    public async resetPassword(code: string, password: string) {
        const { data, message } = await this.agentUserPasswordResetClient.post({
            code,
            password,
        });

        if (message) {
            throw new Error(message);
        }

        return { result: data };
    }
}
